import React, { useState } from "react";
import Chart from "react-apexcharts";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faInfoCircle } from "@fortawesome/pro-regular-svg-icons";
import { getStatusHexColor, getStatusLabel } from "../../helpers/StatusMapper";
import Tooltip from "../../components/Tooltip";
import { useTranslation } from "react-i18next";

import { normalizeStatusText } from "./SiteStatisticsContent";


export default function ConnectorCard({ connector }) {
    const { t } = useTranslation();
    const rawLabels = (connector.statuses ?? []).map((s) => s.status);
    const labels = rawLabels.map((status) => getStatusLabel(normalizeStatusText(status), t));
    const series = (connector.statuses ?? []).map((s) => {
        const n =
            typeof s.percentage === "string"
                ? Number(String(s.percentage).replace(",", "."))
                : Number(s.percentage);
        return Number.isFinite(n) ? n : 0;
    });

    const sessions = Number(connector.totalSessions ?? 0) || 0;
    const successful = Number(connector.successfulSessions ?? 0) || 0;
    const uptime = Number(connector.upTimePercentage ?? 0) || 0;
    const chargerUsage = Number(connector.chargerUsage ?? 0) || 0;
    const successRate = Number(connector.pluginSuccessRate ?? 0) || 0;

    const errorCountTotal = (connector.errors ?? []).reduce(
        (acc, e) => acc + (Number(e.count) || 0),
        0
    );

    const rateColor =
        successRate >= 85
            ? "bg-secondary-600"
            : successRate >= 60
                ? "bg-kraft-500"
                : "bg-error-500";

    return (
        <div className="w-full auto-fit rounded-2xl border border-slate-200 bg-white p-4 shadow-lg">
            {/* HEADER: title left, badge top-right */}
            <div className="mb-3 flex items-start justify-between">
                <div className="text-md font-semibold ">
                    {connector.connectorName}
                </div>

                <HealthBadge uptime={uptime} errorCount={errorCountTotal} chargerUsage={chargerUsage} />
            </div>

            {/* CONTENT: donut + KPI */}
            <div className="grid grid-cols-2 gap-5 items-center">
                {/* LEFT: Donut */}
                <div className="flex justify-center">
                    <div className="relative">
                        <Chart
                            type="donut"
                            height={250}
                            options={donutOptions(labels, rawLabels)}
                            series={series}
                        />

                        <div className="absolute inset-0 grid place-items-center pointer-events-none">
                            <div className="pointer-events-auto relative text-center leading-tight group cursor-help">
                                <div className="text-sm flex items-center justify-center gap-1">
                                    {t("siteStats.uptime")}
                                </div>

                                <div className="text-lg font-semibold">{uptime.toFixed(0)}%</div>

                                <div className="pointer-events-none absolute left-1/2 top-full z-20 mt-2 w-72 -translate-x-1/2 rounded-xl bg-slate-900 px-3 py-2 text-xs text-white opacity-0 shadow-xl transition group-hover:opacity-100">
                                    {t("siteStats.uptimeTooltip")}
                                    <br />
                                    <br />
                                    <b>{t("siteStats.uptimeExcluded")}</b>
                                    <br />
                                    <b>{t("siteStats.uptimeIncluded")}</b>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* RIGHT: KPI column */}
                <div className="flex flex-col gap-3">


                    <div className="rounded-xl border border-slate-200 bg-white px-3 py-3">

                        <div className="text-xs mb-2">{t("entity.sessions")}</div>

                        <div className="space-y-1">

                            <div className="flex justify-between text-sm">
                                <span>{t("siteStats.total")}:</span>
                                <span className="font-semibold">{sessions}</span>
                            </div>

                            <div className="flex justify-between text-sm">
                                <span>{t("siteStats.successful")}:</span>
                                <span className="font-semibold">{successful}</span>
                            </div>

                            <div className="flex justify-between text-sm pt-1">
                                <span className="inline-flex items-center gap-1.5">
                                    {t("siteStats.rate")}:
                                    <Tooltip
                                        content={t("siteStats.pluginSuccessRateTooltip")}
                                        position="right"
                                        tooltipClassName="max-w-sm"
                                    >
                                        <span
                                            className="inline-flex shrink-0 cursor-help text-mute-400 hover:text-mute-600"
                                            aria-label={t("siteStats.pluginSuccessRateTooltip")}
                                        >
                                            <FontAwesomeIcon icon={faInfoCircle} className="h-3.5 w-3.5" />
                                        </span>
                                    </Tooltip>
                                </span>
                                <span className="font-semibold">{successRate.toFixed(0)}%</span>
                            </div>

                        </div>

                        <div className="mt-1 h-2 w-full rounded-full bg-slate-200">
                            <div
                                className={`h-2 rounded-full ${rateColor}`}
                                style={{
                                    width: `${Math.max(0, Math.min(100, successRate))}%`
                                }}
                            />
                        </div>

                    </div>

                </div>

            </div>

            {/* ERRORS */}
            <div className="mt-4 border-t border-slate-200 pt-3">
                <div className="mb-2 text-xs">{t("siteStats.errorCodes")}</div>

                {(connector.errors ?? []).length === 0 ? (
                    <div className="text-sm">{t("siteStats.noErrors")}</div>
                ) : (
                    <div className="space-y-2">
                            <ConnectorErrors connector={connector} />
                    </div>
                )}
            </div>
        </div>
    );
}


/* Info badge */
function InfoBadge({ text }) {
    if (!text) return null;

    return (
        <span className="relative inline-flex">
            <span
                tabIndex={0}
                className="peer inline-flex h-5 w-5 items-center justify-center rounded-full bg-slate-200 text-xs font-semibold text-slate-700 hover:bg-slate-300"
            >
                i
            </span>

            <span className="pointer-events-none absolute right-0 top-full z-50 mt-2 w-56 translate-y-[-4px] rounded-lg bg-slate-900 px-3 py-2 text-xs text-white opacity-0 shadow-xl transition peer-hover:translate-y-0 peer-hover:opacity-100 peer-focus:translate-y-0 peer-focus:opacity-100">
                {text}
            </span>
        </span>
    );
}

function HealthBadge({ uptime, errorCount, chargerUsage }) {
    const { t } = useTranslation();
    const u = Number.isFinite(Number(uptime)) ? Number(uptime) : 0;
    const e = Number.isFinite(Number(errorCount)) ? Number(errorCount) : 0;
    const c = Number.isFinite(Number(chargerUsage)) ? Number(chargerUsage) : 0;

    let level = "ok";
    let reason = "";


    if (u > 95 && c < 5) {
        level = "insecure";
        reason = t("siteStats.healthInsecure", { uptime: u.toFixed(0), usage: c.toFixed(0) });
    } else if (e > 2 && u < 70) {
        level = "critical";
        reason = t("siteStats.healthCritical", { uptime: u.toFixed(0), errors: e });
    } else if (e > 2) {
        level = "warning";
        reason = t("siteStats.healthErrors", { errors: e });
    } else if (u < 80) {
        level = "warning";
        reason = t("siteStats.healthUptime", { uptime: u.toFixed(0) });
    } else {
        level = "ok";
        reason = t("siteStats.healthOk", { uptime: u.toFixed(0) });
    }

    const cfg = {
        ok: {
            label: t("siteStats.healthy"),
            cls: "bg-secondary-50 text-secondary-700 border-secondary-200",
        },
        warning: {
            label: t("siteStats.attention"),
            cls: "bg-kraft-50 text-kraft-700 border-kraft-200",
        },
        critical: {
            label: t("siteStats.critical"),
            cls: "bg-error-50 text-error-700 border-error-200",
        },
        insecure: {
            label: t("siteStats.insecure"),
            cls: "bg-mute-100 text-mute-700 border-mute-300",
        },
    }[level];

    return (
        <span className="relative inline-flex">
            <span
                tabIndex={0}
                className={`peer inline-flex items-center gap-2 rounded-full border px-2.5 py-1 text-[12px] font-semibold cursor-default ${cfg.cls}`}
            >
                <span className="h-2 w-2 rounded-full bg-current opacity-70" />
                {cfg.label}
            </span>

            <span className="pointer-events-none absolute right-0 top-8 z-20 w-64 translate-y-[-4px] rounded-xl bg-slate-900 px-3 py-2 text-[12px] text-white opacity-0 shadow-xl transition peer-hover:translate-y-0 peer-hover:opacity-100 peer-focus:translate-y-0 peer-focus:opacity-100">
                {reason}
            </span>
        </span>
    );
}

function ConnectorErrors({ connector }) {
    const { t } = useTranslation();
    const [expanded, setExpanded] = useState(false);

    const errors = connector.errors ?? [];
    const visibleErrors = expanded ? errors : errors.slice(0, 3);

    return (
        <div className="space-y-2">

            {/* ERROR LIST */}
            <div className="overflow-visible">
                {visibleErrors.map((e, idx) => (
                    <div
                        key={`${e.code}-${idx}`}
                        className="flex items-center justify-between rounded-xl border border-slate-100 bg-slate-50 px-3 py-2 transition hover:bg-slate-100"
                    >

                        <div className="min-w-0">
                            <div className="truncate text-xs font-semibold">
                                {e.code}
                            </div>
                        </div>

                        <div className="flex items-center gap-2">

                            <span className="rounded-lg border border-slate-200 bg-white px-2 py-0.5 text-xs">
                                {e.count}
                            </span>

                            <InfoBadge text={<>{e.code}<br />{e.info}</>} />
                        </div>

                    </div>
                ))}

            </div>

            {/* EXPAND BUTTON */}
            {errors.length > 3 && (
                <button
                    onClick={() => setExpanded(!expanded)}
                    className="group flex w-full items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-medium text-slate-600 transition hover:bg-slate-50 hover:text-slate-900"
                >
                    {expanded ? t("siteStats.showLess") : t("siteStats.showMoreErrors", { count: errors.length - 3 })}

                    <FontAwesomeIcon
                        icon={faChevronDown}
                        className={`transition-transform duration-300 ${expanded ? "rotate-180" : ""
                            }`}
                    />
                </button>
            )}

        </div>
    );
}

function donutOptions(labels, rawLabels) {
    return {
        chart: { type: "donut", sparkline: { enabled: true } },
        labels,
        colors: rawLabels.map((l) =>
            getStatusHexColor(normalizeStatusText(l))
        ),
        legend: { show: false },
        stroke: { width: 0 },
        dataLabels: { enabled: false },
        plotOptions: { pie: { donut: { size: "60%" } } },
        tooltip: {
            y: {
                formatter: (val) => `${val}%`
            }
        },
    };
}
                                    